import {
  Alert,
  Badge,
  Box,
  Center,
  Checkbox,
  CloseButton,
  Divider,
  Group,
  Loader,
  Modal,
  Select,
  Stack,
  Text,
  TextInput,
  Tooltip,
} from '@mantine/core'
import { modals } from '@mantine/modals'
import { Button, EmptyStateBox } from 'components'
import { IconAlertCircle, IconSearch, IconShare, IconUsers } from '@tabler/icons-react'
import type { FC } from 'react'
import { useCallback, useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useNavigate } from 'react-router-dom'
import { ARC_MOSAICO_ENDPOINTS } from 'api/arcMosaico'
import { api } from 'helpers'
import type { ShareWithTeachersModalProps } from './interfaces'
import { ShareSelectItem } from './ShareSelectItem'
import { useShareWithTeachers } from './useShareWithTeachers'

const TUTOR_LIST_MAX_HEIGHT = 320
const SELECT_MIN_WIDTH = 240

export const ShareWithTeachersModal: FC<ShareWithTeachersModalProps> = ({
  opened,
  onClose,
  projectId,
  copyOnly,
}) => {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const [canManage, setCanManage] = useState(false)

  useEffect(() => {
    if (!opened) return
    api.get(ARC_MOSAICO_ENDPOINTS.tutors.list, { params: { page: 1, limit: 1 } })
      .then(r => setCanManage(r.status === 200))
      .catch(() => setCanManage(false))
  }, [opened])

  const {
    search,
    setSearch,
    selectedIds,
    filteredTutors,
    isLoadingTutors,
    hasTutors,
    selections,
    getShareType,
    getShareTypeData,
    isCoTeacher,
    isAllSelected,
    isIndeterminate,
    isSubmitting,
    error,
    handleSelectAll,
    toggleTutor,
    setType,
    handleSubmit,
    clearError,
  } = useShareWithTeachers(projectId, opened, onClose)

  const handleClose = useCallback(() => {
    if (selectedIds.length > 0) {
      modals.openConfirmModal({
        title: t('arc_projects.share_confirm_discard_title'),
        children: <Text size="sm">{t('arc_projects.share_confirm_discard')}</Text>,
        labels: {
          confirm: t('arc_projects.share_discard'),
          cancel: t('arc_projects.share_keep_editing'),
        },
        confirmProps: { color: 'red' },
        centered: true,
        onConfirm: onClose,
      })
      return
    }
    onClose()
  }, [selectedIds, t, onClose])

  return (
    <Modal
      opened={opened}
      onClose={handleClose}
      title={t('arc_projects.share_modal_title')}
      size="lg"
      padding="lg"
      centered
    >
      <Stack spacing="md">
        {error && (
          <Alert
            icon={<IconAlertCircle size={16} />}
            color="red"
            withCloseButton
            onClose={clearError}
          >
            {error}
          </Alert>
        )}

        {isLoadingTutors ? (
          <Center py="xl">
            <Loader size="sm" />
          </Center>
        ) : !hasTutors ? (
          <EmptyStateBox
            withBorder
            icon={<IconUsers size={32} color="gray" stroke={1.5} />}
            text={t('arc_projects.share_no_teachers_description')}
            actionButton={
              <Tooltip
                label={t('arc_projects.share_manage_faculty_no_permission')}
                disabled={canManage}
                withArrow
              >
                <span>
                  <Button
                    variant="filled"
                    color="teal"
                    radius="xl"
                    disabled={!canManage}
                    onClick={() => {
                      onClose()
                      navigate('/teacher/manage-faculty')
                    }}
                  >
                    {t('arc_projects.share_manage_faculty')}
                  </Button>
                </span>
              </Tooltip>
            }
          />
        ) : (
          <>
            <TextInput
              icon={<IconSearch size="1.1rem" />}
              radius="xl"
              size="md"
              placeholder={t('arc_projects.share_search_placeholder')}
              value={search}
              onChange={e => setSearch(e.currentTarget.value)}
              rightSectionWidth={42}
              rightSection={
                search !== '' ? (
                  <CloseButton
                    size="md"
                    title={t('general_buttons_filters_clear')}
                    onClick={() => setSearch('')}
                    variant="transparent"
                  />
                ) : null
              }
            />

            {filteredTutors.length === 0 ? (
              <Center py="md">
                <Stack align="center" spacing={4}>
                  <IconSearch size={32} color="gray" stroke={1.5} />
                  <Text size="sm" color="dimmed" align="center">
                    {t('arc_projects.share_no_results', { search })}
                  </Text>
                </Stack>
              </Center>
            ) : (
              <>
                <Group position="apart" noWrap px={4}>
                  <Checkbox
                    label={t('arc_projects.share_select_all')}
                    checked={isAllSelected}
                    indeterminate={isIndeterminate}
                    onChange={handleSelectAll}
                  />
                  <Box sx={{ minWidth: SELECT_MIN_WIDTH }} />
                </Group>

                <Divider />

                <Box sx={{ maxHeight: TUTOR_LIST_MAX_HEIGHT, overflowY: 'auto' }}>
                  <Stack spacing={0}>
                    {filteredTutors.map(tutor => {
                      const alreadyCoTeacher = isCoTeacher(tutor.id)
                      const isSelected = selections.has(tutor.id)
                      return (
                        <Group
                          key={tutor.id}
                          position="apart"
                          noWrap
                          sx={(theme) => ({
                            padding: '4px 4px',
                            borderRadius: theme.radius.sm,
                            backgroundColor: isSelected ? theme.colors.teal[0] : 'transparent',
                            transition: 'background-color 150ms ease',
                          })}
                        >
                          <Group spacing="sm" align="flex-start" noWrap>
                            <Checkbox
                              checked={isSelected}
                              onChange={() => toggleTutor(tutor.id)}
                              mt={2}
                            />
                            <Stack spacing={4}>
                              <Text
                                size="sm"
                                weight={isSelected ? 500 : 400}
                                sx={{ cursor: 'pointer' }}
                                onClick={() => toggleTutor(tutor.id)}
                              >
                                {`${tutor.firstName} ${tutor.lastName}`}
                              </Text>
                              {alreadyCoTeacher && (
                                <Badge size="xs" color="gray" variant="filled" sx={{ alignSelf: 'flex-start' }}>
                                  {t('arc_projects.share_already_co_teacher')}
                                </Badge>
                              )}
                            </Stack>
                          </Group>
                          <Select
                            size="sm"
                            value={copyOnly ? 'copy' : getShareType(tutor.id)}
                            onChange={val => setType(tutor.id, val)}
                            data={copyOnly ? [{ value: 'copy', label: t('arc_projects.share_as_copy') }] : getShareTypeData(tutor.id)}
                            itemComponent={copyOnly ? undefined : ShareSelectItem}
                            disabled={copyOnly}
                            withinPortal
                            sx={{ minWidth: SELECT_MIN_WIDTH }}
                            styles={(theme) => ({
                              input: { cursor: copyOnly ? 'default' : 'pointer' },
                              dropdown: { minWidth: 360 },
                              item: {
                                '&[data-selected]': {
                                  backgroundColor: theme.colors.gray[1],
                                  color: theme.black,
                                },
                                '&[data-selected]:hover': {
                                  backgroundColor: theme.colors.gray[2],
                                },
                              },
                            })}
                          />
                        </Group>
                      )
                    })}
                  </Stack>
                </Box>
              </>
            )}
          </>
        )}

        {hasTutors && (
          <Group position="right">
            <Button variant="default" onClick={handleClose} disabled={isSubmitting}>
              {t('general_buttons_cancel')}
            </Button>
            <Button
              color="teal"
              leftIcon={<IconShare size={16} />}
              onClick={handleSubmit}
              loading={isSubmitting}
              disabled={isSubmitting || selectedIds.length === 0}
            >
              {t('arc_projects.share_button')}
            </Button>
          </Group>
        )}
      </Stack>
    </Modal>
  )
}
